import React from 'react';
import styled from 'styled-components';
import { Box } from '../..';
import sx from '../../sx';
const gridSpaceSizes = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 48,
  7: 64,
  8: 80,
  9: 96,
  10: 112,
  11: 128,
  12: 144
};
/**
 *
 * The Stack component is a low-level primitive component for arranging items in a row or vertical stack, with even spacing between the items.
 *
 * Use the Stack component for small-scale layout tasks and avoiding redundant margin or padding properties that can double the intended space.
 *
 * Use the `size` prop to control the spacing between elements in the Stack component.
 *
 * The spacing between elements in the Stack is controlled using values from the spacing scale, with a default value set at 3 (8px).
 *
 * By default, the Stack component arranges items vertically in a column, but the direction prop can be used to position items rowly in a row as well.
 *
 */
const Stack = ({
  children,
  as = 'div',
  sx: sxProp,
  direction = 'column',
  align,
  justify,
  recursive = false,
  size = 3
}) => {
  let Component;
  if (recursive) {
    Component = RecursiveStack;
  } else {
    Component = NonRecursiveStack;
  }
  return /*#__PURE__*/React.createElement(Component, {
    as: as,
    sx: sxProp,
    size: size,
    direction: direction,
    align: align,
    justify: justify
  }, children);
};
Stack.displayName = "Stack";
const NonRecursiveStack = styled(Box).withConfig({
  displayName: "Stack__NonRecursiveStack",
  componentId: "sc-19ztxzq-0"
})(["display:flex;width:100%;height:", ";flex-direction:", ";justify-content:", ";align-items:", ";& > * + *{margin-block-start:", ";margin-inline-start:", ";}", ""], p => p.direction === 'column' ? 'auto' : 'revert', p => p.direction === 'column' ? 'column' : 'row', p => p.justify === 'center' ? 'center' : p.justify === 'start' ? 'flex-start' : p.justify === 'end' ? 'flex-end' : p.justify === 'between' ? 'space-between' : 'revert', p => p.align === 'center' ? 'center' : p.align === 'start' ? 'flex-start' : p.align === 'end' ? 'flex-end' : 'revert', p => p.direction === 'column' ? gridSpaceSizes[p.size] + 'px' : 'revert', p => p.direction === 'row' ? gridSpaceSizes[p.size] + 'px' : 'revert', sx);
const RecursiveStack = styled(Box).withConfig({
  displayName: "Stack__RecursiveStack",
  componentId: "sc-19ztxzq-1"
})(["width:100%;height:", ";display:flex;flex-direction:", ";gap:", ";justify-content:", ";align-items:", ";& * + *{margin-block-start:", ";margin-inline-start:", ";}", ""], p => p.direction === 'column' ? 'auto' : 'revert', p => p.direction === 'column' ? 'column' : 'row', p => p.direction === 'row' ? gridSpaceSizes[p.size] + 'px' : 'revert', p => p.justify === 'center' ? 'center' : p.justify === 'start' ? 'flex-start' : p.justify === 'end' ? 'flex-end' : p.justify === 'between' ? 'space-between' : 'revert', p => p.align === 'center' ? 'center' : p.align === 'start' ? 'flex-start' : p.align === 'end' ? 'flex-end' : 'revert', p => p.direction === 'column' ? gridSpaceSizes[p.size] + 'px' : 'revert', p => p.direction === 'row' ? gridSpaceSizes[p.size] + 'px' : 'revert', sx);
export default Stack;